import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import type { Device } from '../../../types/device';
import { AdminDeviceSearchInput } from './AdminDeviceSearchInput';
import { AdminDeviceSearchResultRow } from './AdminDeviceSearchResultRow';

export interface AdminDeviceLookupProps {
  /** Selected device id (controlled). */
  deviceId: string;
  onDeviceIdChange: (id: string) => void;
  onDeviceSelected?: (device: Device) => void;
  label?: string;
}

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

/**
 * Typeahead lookup against GET /api/devices/admin/list (existing search param).
 */
export function AdminDeviceLookup({
  deviceId,
  onDeviceIdChange,
  onDeviceSelected,
  label = 'Find device',
}: AdminDeviceLookupProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['admin', 'devices', 'lookup', debounced],
    queryFn: () =>
      api.adminGetDevices({ search: debounced, limit: 12, page: 1 }),
    enabled: debounced.length >= MIN_QUERY,
  });

  const results = data?.devices ?? [];

  return (
    <div className="space-y-2">
      <span className="block text-xs text-slate-400">{label}</span>
      <AdminDeviceSearchInput
        value={query}
        onChange={setQuery}
        aria-label="Search devices to select"
      />
      <label className="block text-xs text-slate-400 mt-3">
        Device ID (X-Device-ID)
        <input
          className="mt-1 w-full max-w-md rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white font-mono"
          value={deviceId}
          onChange={(e) => onDeviceIdChange(e.target.value)}
          placeholder="dev_…"
        />
      </label>
      {debounced.length >= MIN_QUERY && (
        <div className="max-w-xl rounded-lg border border-slate-700 bg-slate-900/50 p-2 space-y-1">
          {isFetching && <p className="text-xs text-slate-500 px-2 py-1">Searching…</p>}
          {isError && <p className="text-xs text-red-400 px-2 py-1">Search failed.</p>}
          {!isFetching && !isError && results.length === 0 && (
            <p className="text-xs text-slate-500 px-2 py-1">No devices match.</p>
          )}
          {results.map((d) => (
            <AdminDeviceSearchResultRow
              key={d.id}
              device={d}
              onSelect={(device) => {
                onDeviceIdChange(device.id);
                onDeviceSelected?.(device);
                setQuery('');
                setDebounced('');
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
